/**
 * Boot: Phaser game, chat panel, cubicle chrome, Act 1 coordinator.
 */
import * as Phaser from "phaser";
import { GameScene, GAME_WIDTH, GAME_HEIGHT } from "../scene/src/scenes/GameScene";
import { initShop } from "./ui/shop";
import { initChatPanel } from "../scene-harness/src/ui/chatPanel";
import { initCubicle, type CubicleApi } from "../cubicle/src/cubicle";
import { mountTitleCard } from "../scene-harness/src/ui/titleCard";
import { startAct1 } from "../scene-harness/src/act1";
import { startAct2 } from "../act2/src/act2";
import "../act2/src/act2.css";

// ─── Keyboard capture shim ──────────────────────────────────────────
// Phaser's KeyboardManager calls preventDefault on captured keys,
// swallowing typed input. Stop those events before Phaser sees them
// when the target is a text input.
window.addEventListener(
  "keydown",
  (e) => {
    const t = e.target as HTMLElement | null;
    if (!t) return;
    const tag = t.tagName;
    if (tag === "INPUT" || tag === "TEXTAREA" || t.isContentEditable) {
      e.stopImmediatePropagation();
    }
  },
  { capture: true },
);

const config: Phaser.Types.Core.GameConfig = {
  type: Phaser.AUTO,
  parent: "game",
  width: GAME_WIDTH,
  height: GAME_HEIGHT,
  backgroundColor: "#05060a",
  scale: {
    mode: Phaser.Scale.FIT,
    autoCenter: Phaser.Scale.CENTER_BOTH,
  },
  scene: [GameScene],
};

const game = new Phaser.Game(config);

declare global {
  interface Window {
    OALSO?: {
      game: Phaser.Game;
      cubicle?: CubicleApi;
    };
  }
}

window.OALSO = { game };

game.events.once(Phaser.Core.Events.READY, () => {
  const scene = game.scene.getScene("GameScene") as GameScene;

  const cubicle = initCubicle(scene);
  if (!cubicle) throw new Error("cubicle init returned null");
  if (window.OALSO) window.OALSO.cubicle = cubicle;

  const chatApi = initChatPanel();
  initShop(scene, cubicle);

  const titleCard = mountTitleCard();
  const act1 = startAct1({ chat: chatApi, scene, cubicle });

  void (async () => {
    await titleCard.dismiss();
    await act1.run();

    // Act 2 takes the surface. Repo-beat overlay (zIndex 9999) is still
    // mounted; act2-root sits above it at 10000.
    game.destroy(true, false);
    document.getElementById("cubicle")?.style.setProperty("display", "none");
    document.getElementById("app")?.style.setProperty("display", "none");

    const act2Root = document.createElement("div");
    Object.assign(act2Root.style, {
      position: "fixed",
      inset: "0",
      zIndex: "10000",
      background: "#07071a",
    } as CSSStyleDeclaration);
    document.body.append(act2Root);

    const playerState = await startAct2(act2Root);
    console.log("Act 2 complete", playerState);
  })();
});
